import { useEffect } from "react";
import { trackMediaError, trackConvivaCustomEvent, getCustomContextMetadata } from "../../../shared/analytics/media";
import { trackGenericAction } from "../../../shared/analytics/dataLayer";
import middleware from "../../../shared/middleware";
import { getPlaybackErrorMessageInfo } from "../../../shared/utils/playbackHelpers";
import { ANALYTICS_EVENT_TYPES } from "../../../shared/constants/analytics";
import errorConstants from "../../../shared/constants/error";
const { ERROR_TYPES } = errorConstants;
import { usePlayerState } from "./playerState";
import { DT_ATTRIBUTES } from "../../../shared/constants/dynatrace";
import PlayerConstants from "../../../shared/constants/player";

const { bookmarkAVSVideo } = middleware;
const { NO_BROWSER_SUPPORT_ERROR_CODES } = PlayerConstants;

export const usePlaybackErrorEffect = (
  playbackError,
  translate,
  getMediaNodeMetaData,
  type,
  isConvivaAppTrackerEnabled,
  activeContentId,
  hasCalledStopContent
) => {
  const { appProvider, isInHome, userProfile, featureToggles } = usePlayerState();
  const { isDynatraceTrackingEnabled } = featureToggles;
  useEffect(() => {
    if (playbackError?.code || playbackError?.message) {
      const { errorTitle, errorMessage } = getPlaybackErrorMessageInfo(playbackError);
      const message = errorMessage ? translate(errorMessage) : "";
      const errorDetails = errorTitle ? translate(errorTitle) : "";

      if (message && errorDetails) {
        const { metadata } = getMediaNodeMetaData() || {};
        // Sliencing 4032 error code since player retry handles this failure automatically and relaunches playback
        if (!NO_BROWSER_SUPPORT_ERROR_CODES.includes(playbackError.code)) {
          const dynatraceErrorTelemetry = {
            [DT_ATTRIBUTES.ERROR_CODE]: String(playbackError.code),
            [DT_ATTRIBUTES.ERROR_MESSAGE]: playbackError.message,
            [DT_ATTRIBUTES.PLAYBACK_ERROR_SHOWN_TO_USER]: errorDetails,
            [DT_ATTRIBUTES.CONTENT_ID]: String(metadata?.contentId),
            [DT_ATTRIBUTES.PROGRAM_NAME]: metadata?.title,
            [DT_ATTRIBUTES.CHANNEL_ID]: metadata?.channel?.id,
          };
          trackMediaError(playbackError.code, dynatraceErrorTelemetry, isDynatraceTrackingEnabled);
        }
        trackGenericAction(
          ANALYTICS_EVENT_TYPES.PLAYBACK_ERROR,
          {
            errorCode: playbackError.code,
            errorDetails,
            errorMessage: message,
            media: getMediaNodeMetaData(),
          },
          isDynatraceTrackingEnabled
        );
        if (isConvivaAppTrackerEnabled) {
          trackConvivaCustomEvent(
            ANALYTICS_EVENT_TYPES.PLAYBACK_ERROR,
            getCustomContextMetadata(getMediaNodeMetaData(), userProfile, appProvider, isInHome)
          );
        }
        if (appProvider && activeContentId.current && !hasCalledStopContent.current) {
          const contentId = activeContentId.current;
          const requestBody = {
            deltaThreshold: null,
          };
          bookmarkAVSVideo(appProvider, contentId, type, requestBody)
            .catch((err) => {
              console.error(err);
            })
            .finally(() => {
              hasCalledStopContent.current = true;
            });
        }
      }
    }
  }, [
    playbackError,
    translate,
    getMediaNodeMetaData,
    appProvider,
    type,
    isInHome,
    userProfile,
    isConvivaAppTrackerEnabled,
    activeContentId,
    hasCalledStopContent,
    isDynatraceTrackingEnabled,
  ]);
};

export const handlePanicLiveSeeking = (onPlaybackStateChange, translate) => {
  onPlaybackStateChange({
    errorType: ERROR_TYPES.ERROR,
    message: translate("trickplays_unavailable"),
    code: 99999,
  });
};
